import { useState } from "react";
import LightweightChart from "../components/LightweightChart";

// All available symbols
const ALL_SYMBOLS = [
  { symbol: "BTC/USDT", name: "Bitcoin" },
  { symbol: "ETH/USDT", name: "Ethereum" },
  { symbol: "SOL/USDT", name: "Solana" },
  { symbol: "BNB/USDT", name: "BNB" },
  { symbol: "XRP/USDT", name: "XRP" },
  { symbol: "ADA/USDT", name: "Cardano" },
  { symbol: "DOGE/USDT", name: "Dogecoin" },
  { symbol: "AVAX/USDT", name: "Avalanche" },
  { symbol: "DOT/USDT", name: "Polkadot" },
  { symbol: "LINK/USDT", name: "Chainlink" },
  { symbol: "MSTR", name: "MicroStrategy" },
  { symbol: "NVDA", name: "NVIDIA" },
  { symbol: "AMD", name: "AMD" },
  { symbol: "AAPL", name: "Apple" },
  { symbol: "MSFT", name: "Microsoft" },
  { symbol: "GOOGL", name: "Google" },
  { symbol: "AMZN", name: "Amazon" },
  { symbol: "TSLA", name: "Tesla" },
  { symbol: "META", name: "Meta" },
  { symbol: "TLT", name: "20Y Treasury Bond" },
  { symbol: "TLT", name: "iShares 20+ Yr Treasury" },
];

// Timeframes
const TIMEFRAMES = [
  { label: "7D", days: 7 },
  { label: "30D", days: 30 },
  { label: "90D", days: 90 },
  { label: "180D", days: 180 },
  { label: "1Y", days: 365 },
];

export default function TATools() {
  const [selectedSymbol, setSelectedSymbol] = useState("BTC/USDT");
  const [selectedDays, setSelectedDays] = useState(90);

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold">📊 TA Tools</h1>
        <p className="text-gray-400 text-sm mt-1">Advanced charts with AI-powered analysis</p>
      </div>

      {/* Drawing Tools Info */}
      <div className="mb-4 p-3 bg-blue-900/30 border border-blue-700 rounded-lg">
        <p className="text-blue-300 text-sm">
          ✏️ <strong>Drawing Tools:</strong> For advanced drawing tools (trendlines, fibonacci, shapes), 
          use the <a href="/ta" className="underline hover:text-blue-200">TA page</a> which has the full TradingView widget.
        </p>
      </div>

      {/* Symbol tabs */}
      <div className="flex gap-2 flex-wrap mb-4">
        {ALL_SYMBOLS.map((s) => (
          <button
            key={s.symbol}
            onClick={() => setSelectedSymbol(s.symbol)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              selectedSymbol === s.symbol
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            {s.symbol}
          </button>
        ))}
      </div>

      {/* Timeframe selector */}
      <div className="flex gap-2 mb-4">
        <span className="text-gray-400 text-sm self-center mr-2">Range:</span>
        {TIMEFRAMES.map((tf) => (
          <button
            key={tf.days}
            onClick={() => setSelectedDays(tf.days)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              selectedDays === tf.days
                ? "bg-green-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            {tf.label}
          </button>
        ))}
      </div>

      {/* Chart with AI Analysis */}
      <LightweightChart symbol={selectedSymbol} height={500} />

      {/* Info */}
      <div className="mt-6 bg-gray-800 rounded-xl border border-gray-700 p-4">
        <h2 className="text-lg font-semibold mb-3">📈 Technical Indicators</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="bg-gray-900/50 p-3 rounded-lg">
            <p className="text-yellow-400 font-medium mb-1">🟡 SMA (Simple Moving Average)</p>
            <p className="text-gray-400">Average price over a period. 20/50/200 day SMAs are key levels.</p>
          </div>
          <div className="bg-gray-900/50 p-3 rounded-lg">
            <p className="text-purple-400 font-medium mb-1">🟣 Bollinger Bands</p>
            <p className="text-gray-400">Price envelope at 2 std deviations. Squeeze = low volatility, breakout imminent.</p>
          </div>
          <div className="bg-gray-900/50 p-3 rounded-lg">
            <p className="text-blue-400 font-medium mb-1">🔵 RSI (Relative Strength Index)</p>
            <p className="text-gray-400">Momentum 0-100. &lt;30 oversold, &gt;70 overbought.</p>
          </div>
          <div className="bg-gray-900/50 p-3 rounded-lg">
            <p className="text-cyan-400 font-medium mb-1">🔷 MACD</p>
            <p className="text-gray-400">Trend/momentum. Histogram positive = bullish, negative = bearish.</p>
          </div>
        </div>
      </div>
    </div>
  );
}